import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
interface LocalizationResponse {
  [localKey: string]: string;
}

/** One entry of `GET /localization/v1/countries` (fields AMIE uses). */
export interface LocalizationCountry {
  id: string;
  /** Nullable in real data (test env: id 207 / ASM has `name: null`). */
  name: string | null;
  countryCode2: string;
  countryCode3: string;
}

@Injectable({ providedIn: 'root' })
export class LocalizationService {
  private readonly http = inject(HttpClient);
  private readonly cache = new Map<string, Map<string, string>>();
  private countries$?: Observable<LocalizationCountry[]>;

  /**
   * The full country list — old CMT `LocalizationService.getCountries()`, loaded once per session
   * and the source of the number dialog's nationality picker (independent of the number
   * inventory). A failed load yields `[]` and is retried on the next call, as old CMT resolves [].
   * @ticket EB-3809
   */
  getCountries(): Observable<LocalizationCountry[]> {
    this.countries$ ??= this.http.get<LocalizationCountry[]>(`${environment.localizationV1Url}/countries`).pipe(
      catchError(() => {
        this.countries$ = undefined;
        return of([] as LocalizationCountry[]);
      }),
      shareReplay(1),
    );
    return this.countries$;
  }

  getTranslations(
    brandId: number,
    language: string,
    globalKey: string,
    category: string
  ): Observable<Map<string, string>> {
    const cacheKey = `${brandId}-${language}-${globalKey}-${category}`;
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    const params = new HttpParams()
      .set('language', language)
      .set('brandId', brandId.toString())
      .set('globalKey', globalKey)
      .set('category', category);

    return this.http.get<LocalizationResponse>(`${environment.localizationV1Url}/i18n`, { params }).pipe(
      map((response) => new Map<string, string>(Object.entries(response))),
      tap((translations) => this.cache.set(cacheKey, translations)),
      catchError(() => of(new Map<string, string>()))
    );
  }

  getTranslation(
    brandId: number,
    language: string,
    globalKey: string,
    category: string,
    localKey: string,
    fallback = ''
  ): Observable<string> {
    return this.getTranslations(brandId, language, globalKey, category).pipe(
      map((translations) => translations.get(localKey) ?? fallback)
    );
  }

  clearCache(): void {
    this.cache.clear();
  }
}
